import React, { useState, useEffect } from 'react';
import { OpenXLogo } from '../../assets';
import FlatButtons from '../FlatButton/FlatButton';
import styles from './style.module.css';
import Typography from '../../components/Typography/Typography';
import { FONT_FAMILIES, FONT_WEIGHTS, FONT_SIZES } from '../../assets/constants/typography';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { logout, checkAuthStatus } from '../../redux/slices/authSlice';
import { StoreUserAvatar } from '../../assets/svgs';

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const isLoggedIn = useAppSelector((state) => state.auth.isAuthenticated);
  const userProfileImage = useAppSelector((state) => state.auth.user?.profileImage);

  useEffect(() => {
    dispatch(checkAuthStatus());
  }, [dispatch, location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setShowOverlay(false);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showProfileMenu && !target.closest(`.${styles.profileContainer}`)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setShowOverlay(!isMobileMenuOpen);
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const Logo = () => (
    <div className={styles.logoContainer}>
      <OpenXLogo
        className={styles.logo}
        onClick={() => {
          navigate('/');
          setIsMobileMenuOpen(false);
          setShowOverlay(false);
        }}
      />
    </div>
  );

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Marketplace', href: '/marketPlace' },
    { name: 'FAQ', href: '/faq' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleNavClick = (href: string) => {
    setIsMobileMenuOpen(false);
    setShowOverlay(false);
    navigate(href);
  };

  const handleLogin = () => {
    navigate('/signIn');
    setIsMobileMenuOpen(false);
    setShowOverlay(false);
  };

  const handleSignup = () => {
    navigate('/createAccount');
    setIsMobileMenuOpen(false);
    setShowOverlay(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    setShowProfileMenu(false);
    setIsMobileMenuOpen(false);
    setShowOverlay(false);
    navigate('/');
  };

  const handleProfile = () => {
    setShowProfileMenu(false);
    setIsMobileMenuOpen(false);
    setShowOverlay(false);
    navigate('/profile');
  };

  return (
    <>
      <nav className={styles.navbar}>
        <div className={styles.navContainer}>
          <div className={styles.logoSection}>
            <Logo />
          </div>

          <div className={styles.desktopNav}>
            <div className={styles.navLinks}>
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className={`${styles.navLink} ${location.pathname === link.href ? styles.activeNavLink : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }}
                >
                  <Typography
                    variant="bodyRegular"
                    fontFamily={FONT_FAMILIES.SATOSHI}
                    weight={FONT_WEIGHTS.REGULAR}
                    size={FONT_SIZES.BASE}
                    className={styles.navLinkText}
                  >
                    {link.name}
                  </Typography>
                </a>
              ))}
            </div>
          </div>

          <div className={styles.desktopAuth}>
            {isLoggedIn ? (
              <div className={styles.profileContainer}>
                <button 
                  className={styles.profileButton}
                  onClick={toggleProfileMenu}
                  aria-label="Profile menu"
                >
                  <div className={styles.profileIcon}>
                    {userProfileImage ? (
                      <img 
                        src={userProfileImage}
                        alt="User Profile" 
                        className={styles.profileImage}
                        onError={(e) => {
                          e.currentTarget.src = StoreUserAvatar;
                        }}
                      />
                    ) : (
                      <img 
                        src={StoreUserAvatar}
                        alt="User Profile" 
                        className={styles.profileImage} 
                      />
                    )}
                  </div>
                </button>
                {showProfileMenu && (
                  <div className={styles.profileDropdown}>
                    <button onClick={handleProfile} className={styles.dropdownItem}>
                      Profile
                    </button>
                    <button onClick={handleLogout} className={styles.dropdownItem}>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <FlatButtons 
                  className={styles.desktopLoginBtn} 
                  onLogin={handleLogin}
                  onClick={handleLogin}
                >
                  <Typography
                    variant='buttonText'
                    fontFamily='satoshi'
                    weight='bold'
                    size='base'
                    className={styles.desktopLoginText}
                  >Login
                  </Typography>
                </FlatButtons>
                <FlatButtons 
                  className={styles.desktopSignupBtn} 
                  onSignup={handleSignup}
                  onClick={handleSignup}
                >
                  <Typography
                    variant='buttonText'
                    fontFamily='satoshi'
                    weight='bold'
                    size='base'
                    className={styles.desktopSignupText}
                  >
                    Sign up
                  </Typography>
                </FlatButtons>
              </>
            )}
          </div>

          <div className={styles.mobileMenuBtn}>
            <button
              type="button"
              onClick={toggleMobileMenu}
              className={styles.hamburgerBtn}
              aria-expanded={isMobileMenuOpen ? 'true' : 'false'}
            >
              <span className={styles.srOnly}>Open main menu</span>
              <div className={styles.hamburgerIcon}>
                <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerLineTop : ''}`}></span>
                <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerLineMiddle : ''}`}></span>
                <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.hamburgerLineBottom : ''}`}></span>
              </div>
            </button>
          </div>
        </div>

        <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuOpen : ''}`}>
          <div className={styles.mobileMenuContent}>
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`${styles.mobileNavLink} ${location.pathname === link.href ? styles.activeMobileNavLink : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
              >
                {link.name}
              </a>
            ))}

            {/* Mobile Auth - Show profile options if logged in */}
            <div className={styles.mobileAuth}>
              {isLoggedIn ? (
                <>
                  <FlatButtons className={styles.mobileProfileBtn} onClick={handleProfile}>
                    <Typography
                      variant='buttonText'
                      fontFamily='satoshi'
                      weight='bold'
                      size='base'
                      className={styles.desktopLoginText}
                    >
                      Profile
                    </Typography>
                  </FlatButtons>
                  <FlatButtons className={styles.mobileLogoutBtn} onClick={handleLogout}>
                    <Typography
                      variant='buttonText'
                      fontFamily='satoshi'
                      weight='bold'
                      size='base'
                      className={styles.desktopSignupText}
                    >
                      Logout
                    </Typography>
                  </FlatButtons>
                </>
              ) : (
                <>
                  <FlatButtons className={styles.mobileLoginBtn} onClick={handleLogin}>
                    <Typography
                      variant='buttonText'
                      fontFamily='satoshi'
                      weight='bold'
                      size='base'
                      className={styles.desktopLoginText}
                    >
                      Login
                    </Typography>
                  </FlatButtons>
                  <FlatButtons className={styles.mobileSignupBtn} onClick={handleSignup}>
                    <Typography
                      variant='buttonText'
                      fontFamily='satoshi'
                      weight='bold'
                      size='base'
                      className={styles.desktopSignupText}
                    >
                      Sign up
                    </Typography>
                  </FlatButtons>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {showOverlay && (
        <div
          className={`${styles.overlay} ${showOverlay ? styles.active : ''}`}
          onClick={() => {
            setIsMobileMenuOpen(false);
            setShowOverlay(false);
          }}
        />
      )}

      <div className={styles.navbarSpacer}></div>
    </>
  );
};